import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type FormikErrors, useFormik } from 'formik';
import { CircularProgress, InputAdornment } from '@mui/material';
import styled from 'styled-components';
import { object, type ObjectSchema, string } from 'yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { PAGE_TEXT } from './lang';
import loginBackground from './assets/login-background.png';
import {
  type UseCaptcha,
  useCaptcha,
  type Captcha,
  type LoginFormValues,
  useAuth,
} from '../../contexts';
import {
  Alert,
  type AlertDetails,
  Button,
  Loading,
  TextField,
  SVGCaptcha,
  IconButton,
} from '../../components';
import { handlePageLoading } from '../../utils';

enum ROLE_ENUM {
  STAFF = 'STAFF',
  APPLICANT = 'APPLICANT',
}

enum CAPTCHA_ENUM {
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
}

type LoginProps = {
  staffLogin: boolean;
  envDisableCaptcha: boolean;
};

export const LoginPage: React.FC<LoginProps> = props => {
  const { staffLogin, envDisableCaptcha } = props;

  const { login, ssoLogin, user, isTokenValid } = useAuth();
  const isValid = isTokenValid();

  const navigate = useNavigate();

  const [pageLoading, setPageLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const publishDate = process.env.REACT_APP_APPLICATION_PUBLISH_DATE as string;
  // if the date is before the september 1 2024, hide the login form
  const presentLoginForm =
    new Date().getTime() >
    new Date(publishDate ?? '2024-08-31T16:00:00.000Z').getTime();

  useEffect(() => {
    // if the user has logged in, redirect to home page
    // if the user is not well initialized, do nothing
    if (user != null && user.role?.length > 0 && isValid) {
      navigate('/home');
    }
  }, []);

  const ROLE_CONFIG = {
    [ROLE_ENUM.APPLICANT]: {
      disableCaptcha: envDisableCaptcha, // captcha of applicant-login depends on env config
      isStaff: false,
    },
    [ROLE_ENUM.STAFF]: {
      disableCaptcha: true, // staff-login does not need captcha
      isStaff: true,
    },
  };

  const CAPTCHA_CONFIG: Record<
    CAPTCHA_ENUM,
    {
      initialValues: LoginFormValues;
      validationSchema: ObjectSchema<any>;
      handleSubmit: (values: LoginFormValues) => Promise<void>;
      useCaptchaFn: () => UseCaptcha;
    }
  > = {
    [CAPTCHA_ENUM.DISABLE]: {
      initialValues: {
        email: '',
        password: '',
      },
      validationSchema: object({
        email: string().required(PAGE_TEXT.REQUIRED_EMAIL),
        password: string().required(PAGE_TEXT.REQUIRED_PASSWORD),
      }),
      handleSubmit: async (values: LoginFormValues): Promise<void> => {
        const { email, password } = values;
        try {
          await login(email, password);
        } catch (error) {
          setOpenAlert(true);
          setAlertDetails({
            alertContent: PAGE_TEXT.LOGIN_FAILED + (error as Error).message,
            alertType: 'error',
          });
        }
      },
      useCaptchaFn: () => {
        return {
          data: undefined,
          loading: false,
          error: undefined,
          refetch: () => {},
        }; // dummy useCaptcha
      },
    },
    [CAPTCHA_ENUM.ENABLE]: {
      initialValues: {
        email: '',
        password: '',
        captcha: { questionId: '', submission: '' },
      },
      validationSchema: object({
        email: string().required(PAGE_TEXT.REQUIRED_EMAIL),
        password: string().required(PAGE_TEXT.REQUIRED_PASSWORD),
        captcha: object({
          submission: string().required(PAGE_TEXT.REQUIRED_CAPTCHA),
        }),
      }),
      handleSubmit: async (values: LoginFormValues): Promise<void> => {
        await handlePageLoading(setPageLoading, async () => {
          const { email, password, captcha } = values;
          await login(email, password, captcha);
        }).catch(error => {
          setAlertDetails({
            alertContent: PAGE_TEXT.LOGIN_FAILED + (error as Error).message,
            alertType: 'error',
          });
          setOpenAlert(true);
          handleRefreshSVG();
        });
      },
      useCaptchaFn: useCaptcha,
    },
  };

  const roleConfig =
    ROLE_CONFIG[staffLogin ? ROLE_ENUM.STAFF : ROLE_ENUM.APPLICANT];

  // CAPTCHA_CONFIG depends on ROLE_CONFIG
  const captchaConfig =
    CAPTCHA_CONFIG[
      roleConfig.disableCaptcha ? CAPTCHA_ENUM.DISABLE : CAPTCHA_ENUM.ENABLE
    ];

  const { initialValues, validationSchema, handleSubmit, useCaptchaFn } =
    captchaConfig;

  const { data: captchaData, refetch: captchaRefetch } = useCaptchaFn();
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleRefreshSVG = (): void => {
    setCaptchaLoading(true);
    captchaRefetch();
  };

  useEffect(() => {
    if (captchaData?.questionId) {
      void formik.setFieldValue('captcha.questionId', captchaData?.questionId);
    }
    if (captchaData?.imageData) {
      setCaptchaLoading(false);
    }
  }, [captchaData?.questionId, captchaData?.imageData]);

  const formikFieldError = {
    email: formik.touched.email && Boolean(formik.errors.email),
    password: formik.touched.password && Boolean(formik.errors.password),
    submission:
      Boolean(formik.touched.captcha) && // for nested formik data
      Boolean((formik.errors.captcha as FormikErrors<Captcha>)?.submission),
  };

  const title: React.ReactNode = (
    <>
      <h1>
        {roleConfig.isStaff ? PAGE_TEXT.LOGIN_TITLE : PAGE_TEXT.RPG_ADMISSION}
      </h1>
      <h2>{PAGE_TEXT.ONLINE_APPLICATION_FOR_PHD_AND_MPHIL_ADMISSION}</h2>
    </>
  );

  const formField: React.ReactNode = !roleConfig.isStaff && (
    <>
      <TextField
        label={PAGE_TEXT.EMAIL}
        id="login-email"
        name="email"
        type="email"
        className="w-[500px]"
        InputLabelProps={{ shrink: true }}
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formikFieldError.email}
        helperText={formikFieldError.email && formik.errors.email}
      />

      <TextField
        label={PAGE_TEXT.PASSWORD}
        id="login-password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        className="w-[500px]"
        InputLabelProps={{ shrink: true }}
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formikFieldError.password}
        helperText={formikFieldError.password && formik.errors.password}
        sx={{
          '.MuiInputBase-root': {
            backgroundColor: '#ffffff',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                color="primary"
                size="large"
                aria-label={
                  showPassword ? 'hide the password' : 'display the password'
                }
                onClick={() => {
                  setShowPassword(!showPassword);
                }}
                edge="end"
                icon={showPassword ? <VisibilityOff /> : <Visibility />}
              />
            </InputAdornment>
          ),
        }}
      />
      {!roleConfig.disableCaptcha && (
        <div className="flex flex-row justify-between w-[500px]">
          <TextField
            fullWidth
            label={PAGE_TEXT.CAPTCHA}
            name="captcha.submission"
            InputLabelProps={{ shrink: true }}
            value={formik.values.captcha?.submission}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formikFieldError.submission}
            helperText={
              formikFieldError.submission &&
              (formik.errors.captcha as FormikErrors<Captcha>)?.submission
            }
          />
          <div className="flex justify-center items-center w-[270px]">
            {!captchaLoading ? (
              captchaData !== undefined && (
                <SVGCaptcha
                  svg={captchaData.imageData}
                  onClick={handleRefreshSVG}
                />
              )
            ) : (
              <CircularProgress
                color="inherit"
                className="!w-[25px] !h-[25px]"
              />
            )}
          </div>
        </div>
      )}
      <div className="w-full text-right">
        <Link to="/forgot-password">{PAGE_TEXT.FORGOT_PASSWORD}</Link>
      </div>
    </>
  );

  const loginButton: React.ReactNode = roleConfig.isStaff ? (
    <Button type="button" variant="contained" onClick={ssoLogin}>
      {PAGE_TEXT.SSO_LOGIN}
    </Button>
  ) : (
    <Button
      type="submit"
      variant="contained"
      fullWidth
      className="mt-4"
      disabled={formik.isSubmitting}
    >
      {PAGE_TEXT.LOGIN}
    </Button>
  );

  const signUpHere: React.ReactNode = !roleConfig.isStaff && (
    <div className="mt-4">
      {PAGE_TEXT.DONT_HAVE_ACCOUNT}
      <Link to="/signup">{PAGE_TEXT.SIGNUP}</Link>
    </div>
  );

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Container className="flex items-center justify-center w-full p-8">
        <Loading openLoading={pageLoading} />
        <form onSubmit={formik.handleSubmit}>
          {presentLoginForm ? (
            <>
              {title}

              <div className="form-field">
                {formField}

                {loginButton}
              </div>

              {signUpHere}
            </>
          ) : (
            <>
              <h1 className="!text-[2.5rem] font-medium">
                {PAGE_TEXT.COMING_SOON}
              </h1>
              <h2>{PAGE_TEXT.AT_SEPTEMBER}</h2>
            </>
          )}
        </form>
        <img
          src={loginBackground}
          className="absolute w-full bottom-28 z-[-1] opacity-70"
        />
      </Container>
    </>
  );
};

const Container = styled.div`
  form {
    width: 45rem;
    min-height: 28rem;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #e9e9e9;
    opacity: 0.9;
    border-radius: 0.5rem;

    h1 {
      margin: 1rem;
      font-size: 1.5rem;
      text-align: center;
      font-weight: bold;
      color: #951d1d;
    }

    h2 {
      font-size: 1rem;
      margin-bottom: 2rem;
      padding: 0;
      font-weight: 500;
      text-align: center;
    }

    input {
      background-color: #ffffff;
      border-radius: 4px;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 1rem;
    }
  }
`;
